// src/hooks/useShopDemoState.js
import { useEffect, useState } from "react";
import { SHOP_STATUSES } from "../constants/shopStatus";

const LS_KEY = "mudo:shop-demo-state";

function readState() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeState(next) {
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

/**
 * Demo UI state for shop listings, keyed by listingId.
 *
 * Shape:
 * {
 *   [listingId]: {
 *     status: SHOP_STATUS,
 *     offerAmount: number | null,
 *     hasOffer: boolean,
 *     roleOverride?: "buyer" | "seller"
 *   }
 * }
 */
export function useShopDemoState() {
  const [state, setState] = useState(() => readState());

  useEffect(() => {
    writeState(state);
  }, [state]);

  const getListingState = (listingId) => {
    return (
      state[listingId] || {
        status: SHOP_STATUSES.LISTED,
        hasOffer: false,
        offerAmount: null,
      }
    );
  };

  const updateListingState = (listingId, updater) => {
    setState((prev) => {
      const current = prev[listingId] || {
        status: SHOP_STATUSES.LISTED,
        hasOffer: false,
        offerAmount: null,
      };
      const next = typeof updater === "function" ? updater(current) : updater;
      return {
        ...prev,
        [listingId]: next,
      };
    });
  };

  return {
    state,
    getListingState,
    updateListingState,
  };
}
